import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Send } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";

import { useAuth } from "@platform/auth/context";
import {
	deleteEndpointMutation,
	sendTestMutation,
	updateEndpointMutation,
} from "../webhooks.mutations";
import type { WebhookEndpoint } from "../webhooks.types";
import { EditWebhookEndpoint } from "./edit-webhook-endpoint";
import { RotateWebhookSecret } from "./rotate-webhook-secret";
import { WebhookErrorText } from "./webhook-error-text";

// Renders nothing for a viewer without the permissions. Deleting drops the delivery log, so an
// alert dialog confirms it; turning an endpoint off can be undone and needs none.
export function WebhookEndpointActions({ endpoint }: { endpoint: WebhookEndpoint }) {
	const t = useTranslations("webhooks.actions");
	const { can } = useAuth();
	const navigate = useNavigate();
	const test = useMutation(sendTestMutation);
	const toggle = useMutation(updateEndpointMutation);
	const remove = useMutation(deleteEndpointMutation);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const canUpdate = can({ webhook: ["update"] });
	const canDelete = can({ webhook: ["delete"] });
	if (!canUpdate && !canDelete) return null;

	const enabled = endpoint.status === "enabled";
	const error = test.error ?? toggle.error ?? remove.error;

	return (
		<div className="flex flex-col items-start gap-2">
			<div className="flex flex-wrap gap-2">
				{canUpdate && enabled && (
					<Button
						size="sm"
						disabled={test.isPending}
						onClick={() => {
							toggle.reset();
							test.mutate(endpoint.id);
						}}
					>
						<Send className="size-4" />
						{t("sendTest")}
					</Button>
				)}
				{canUpdate && <EditWebhookEndpoint endpoint={endpoint} />}
				{canUpdate && <RotateWebhookSecret endpointId={endpoint.id} />}
				{canUpdate && (
					<Button
						variant="outline"
						size="sm"
						disabled={toggle.isPending}
						onClick={() => {
							test.reset();
							toggle.mutate({ id: endpoint.id, enabled: !enabled });
						}}
					>
						{enabled ? t("disable") : t("enable")}
					</Button>
				)}
				{canDelete && (
					<AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
						<AlertDialogTrigger asChild>
							<Button variant="outline" size="sm" className="text-destructive">
								{t("delete")}
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>{t("deleteConfirmTitle")}</AlertDialogTitle>
								<AlertDialogDescription className="break-all">
									{t("deleteConfirmDescription", { url: endpoint.url })}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>{t("deleteConfirmCancel")}</AlertDialogCancel>
								<AlertDialogAction
									variant="destructive"
									onClick={() =>
										remove.mutate(endpoint.id, {
											onSuccess: () => void navigate({ to: "/webhooks" }),
										})
									}
								>
									{t("deleteConfirmConfirm")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				)}
			</div>
			{test.isSuccess && (
				<p role="status" className="text-muted-foreground text-sm">
					{t("testQueued")}
				</p>
			)}
			{error && <WebhookErrorText error={error} />}
		</div>
	);
}
